import { useEffect, useState } from 'react';
import { getDisplayErrorMessage, setSalesStaffServedRegions } from '@/lib/api';
import type { SalesStaffRow } from '@/lib/salesTeam';
import { sameRegions } from '@/lib/regionPicker';
import { RegionChips, RegionPicker } from '@/components/regions/RegionPicker';
import { SalesCard } from './SalesPrimitives';
import styles from './SalesTeam.module.css';

/**
 * An SP Manager's territory as states and provinces (Warren, 25 October
 * 2026: "Give each SP Manager a territory the same way"). The approval screen
 * suggests the SP Manager whose territory covers most of a new partner's
 * regions. Shown on the Sales Staff page, whose list read is Super Admin
 * only, the same check PUT /admin/sales-staff/:id/served-regions makes.
 * English only, like the rest of the new partner region copy.
 */
export function StaffTerritoryCard({ person, onSaved }: { person: SalesStaffRow; onSaved: (message: string) => void }) {
  const saved = person.served_regions ?? [];
  const [draft, setDraft] = useState<string[]>(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setDraft(person.served_regions ?? []); setError(''); }, [person._id, person.served_regions]);
  const isManager = (person.role ?? 'rep') === 'manager';
  async function save() {
    setBusy(true); setError('');
    try {
      const r = await setSalesStaffServedRegions(person._id, draft);
      onSaved(r.unchanged ? 'No change to the territory.' : 'Territory saved.');
    } catch (err) { setError(await getDisplayErrorMessage(err, 'Could not save the territory.')); }
    finally { setBusy(false); }
  }
  return (
    <SalesCard title="Territory (states and provinces)">
      <p className={styles.muted}>
        {isManager
          ? 'Recorded for reference. New partner suggestions come from SP Managers only, and this person is a Sales Manager.'
          : 'When a new Sales Partner is approved, the approval screen suggests the SP Manager whose territory covers the most of the applicant’s regions. A Super Admin confirms; nothing is assigned automatically.'}
      </p>
      <div className={styles.stack}>
        <RegionChips codes={draft} empty="No territory recorded." />
        <RegionPicker value={draft} onChange={setDraft} label="Territory" emptyLabel="Choose states and provinces" disabled={busy} />
        <div>
          <button type="button" className={styles.button} disabled={busy || sameRegions(draft, saved)} onClick={() => void save()}>
            {busy ? 'Saving' : 'Save territory'}
          </button>
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
      </div>
    </SalesCard>
  );
}
